import { io } from "socket.io-client";
import { baseURL } from "../../API";

const getSocketUrl = () => {
  try {
    return `${new URL(baseURL).origin}/conversation-corner`;
  } catch (_) {
    return "https://staging-api.cinchy.me/conversation-corner";
  }
};

const URL = getSocketUrl();

const HEALTH_CHECK_INTERVAL_MS = 5000;
const USERID_POLL_INTERVAL_MS = 1000;
const MANUAL_RECONNECT_COOLDOWN_MS = 5000;
const EXPORT_WATCH_INTERVAL_MS = 2000;
const REPORT_EXPORT_TIMEOUT_MS = 180000;

const getUserId = () => {
  return localStorage.getItem("userId") || localStorage.getItem("userID");
};

let socket = null;
let userIdPollTimer = null;
let healthCheckTimer = null;
let serverReconnectTimer = null;
let lastManualConnectTs = 0;
/** Room the app wants to be in (survives disconnect). */
let desiredRoomId = null;
/** Room we last successfully emitted join-room for while connected. */
let activelyJoinedRoomId = null;
/** userId from the handshake of the current connection. */
let connectedUserId = null;
/** True while a report file is expected on the socket. */
let exportWatchActive = false;
let exportWatchTimer = null;
let pendingExportFileType = null;

const applyUserIdToSocket = (s, userId) => {
  if (!s || !userId) return;

  const currentAuthId = s.auth?.userId;
  const currentQueryId = s.io?.opts?.query?.userId;
  if (currentAuthId === userId && currentQueryId === userId) {
    return;
  }

  s.auth = { ...(s.auth || {}), userId };
  if (s.io && s.io.opts) {
    s.io.opts.query = { ...(s.io.opts.query || {}), userId };
  }
};

const isSocketBusy = (s) => {
  if (!s) return false;
  if (s.connected) return true;
  // Handshake already in progress. A second connect() starts another poll.
  if (s.io?._readyState === "opening") return true;
  if (s.io?._reconnecting) return true;
  return false;
};

const safeConnect = (s, reason = "manual") => {
  if (!s) return;
  if (isSocketBusy(s)) return;

  const now = Date.now();
  if (now - lastManualConnectTs < MANUAL_RECONNECT_COOLDOWN_MS) return;

  lastManualConnectTs = now;
  try {
    console.log(`🔄 socket connect (${reason})`);
    s.connect();
  } catch (_) {}
};

const emitJoinIfNeeded = (s = socket) => {
  if (!s || !desiredRoomId) return;
  if (!s.connected) return;
  if (activelyJoinedRoomId === desiredRoomId) {
    return;
  }

  if (activelyJoinedRoomId && activelyJoinedRoomId !== desiredRoomId) {
    console.log("📤 leave-room:", activelyJoinedRoomId);
    s.emit("leave-room", activelyJoinedRoomId);
  }

  console.log("📤 join-room:", desiredRoomId);
  s.emit("join-room", desiredRoomId);
  activelyJoinedRoomId = desiredRoomId;
};

const decodeBase64Chunks = (cleaned) => {
  const parts = [];
  const chunkSize = 512 * 1024;
  for (let offset = 0; offset < cleaned.length; offset += chunkSize) {
    const slice = cleaned.slice(offset, offset + chunkSize);
    const binary = window.atob(slice);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    parts.push(bytes);
  }
  return parts;
};

const downloadBase64File = ({ base64, fileName, contentType, fileType }) => {
  const cleaned = String(base64 || "")
    .replace(/^data:.*;base64,/, "")
    .replace(/\s/g, "")
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const padded = cleaned.length % 4 === 0 ? cleaned : cleaned + "=".repeat(4 - (cleaned.length % 4));
  const blob = new Blob(decodeBase64Chunks(padded), {
    type:
      contentType ||
      (fileType === "pdf"
        ? "application/pdf"
        : fileType === "csv"
          ? "text/csv"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"),
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || `export.${fileType || "xlsx"}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoking immediately can cancel a large PDF/Excel download before the browser reads it.
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

const EXPORT_DOWNLOAD_EVENTS = {
  mobilisation: "cinchy-gates-export-downloaded",
  report: "cinchy-report-export-downloaded",
};

const EXPORT_FAILED_EVENTS = {
  mobilisation: "cinchy-gates-export-failed",
  report: "cinchy-report-export-failed",
};

const EXPORT_SOCKET_EVENTS = [
  "export-completed",
  "exportCompleted",
  "export_completed",
];

const asRecord = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (_) {
      return null;
    }
  }
  return typeof value === "object" ? value : null;
};

const looksLikeBase64 = (value) =>
  typeof value === "string" &&
  value.length > 80 &&
  /^[A-Za-z0-9+/=\r\n]+$/.test(value.slice(0, 240));

const pickBase64 = (record) => {
  const candidates = [
    record.base64,
    record.fileBase64,
    record.fileContent,
    record.buffer,
    record.content,
  ];
  return candidates.find((value) => looksLikeBase64(value)) || null;
};

const normalizeFileType = (value) => {
  const type = String(value || "").toLowerCase();
  if (type === "pdf") return "pdf";
  if (type === "xls" || type === "xlsx" || type === "excel" || type === "csv") {
    return type === "xls" || type === "excel" ? "xlsx" : type;
  }
  return "";
};

export const readExportPayload = (payload) => {
  const root = asRecord(payload);
  if (!root) return null;

  const queue = [root];
  const seen = new Set();
  while (queue.length) {
    const current = queue.shift();
    if (!current || seen.has(current)) continue;
    seen.add(current);

    const base64 = pickBase64(current);
    if (base64) {
      return {
        ...current,
        base64,
        fileName: current.fileName || current.filename || current.name,
        fileType: normalizeFileType(current.fileType || current.doctype || current.format),
        contentType: current.contentType || current.mimeType || current.mimetype,
        type: current.type,
      };
    }

    ["data", "payload", "result", "file", "export", "body"].forEach((key) => {
      const child = asRecord(current[key]);
      if (child) queue.push(child);
    });
  }

  return null;
};

const exportIdentity = (data) =>
  `${data?.type || ""}:${data?.fileName || ""}:${data?.base64?.length || 0}`;

/** Decode a mobilisation or report export and start the browser download once. */
export const downloadSocketExport = (payload) => {
  const parsed = readExportPayload(payload);
  if (!parsed?.base64) return false;

  const explicitType =
    parsed.type === "mobilisation" || parsed.type === "report" ? parsed.type : "";
  const fileType =
    normalizeFileType(parsed.fileType) ||
    normalizeFileType(parsed.type) ||
    pendingExportFileType ||
    "";
  const inferredType =
    explicitType ||
    (String(parsed.fileName || "").toLowerCase().includes("mobilisation")
      ? "mobilisation"
      : "report");
  const data = {
    ...parsed,
    type: inferredType,
    fileType: fileType || (inferredType === "mobilisation" ? "csv" : "xlsx"),
    fileName:
      parsed.fileName ||
      `report.${fileType || (inferredType === "mobilisation" ? "csv" : "xlsx")}`,
  };
  const eventName = EXPORT_DOWNLOAD_EVENTS[data.type];
  if (!eventName) return false;
  if (typeof window === "undefined") return false;

  const identity = exportIdentity(data);
  if (window.__cinchyLastSocketExport === identity) return false;
  window.__cinchyLastSocketExport = identity;

  try {
    downloadBase64File(data);
  } catch (error) {
    if (window.__cinchyLastSocketExport === identity) {
      window.__cinchyLastSocketExport = null;
    }
    throw error;
  }
  window.dispatchEvent(new CustomEvent(eventName, { detail: data }));
  return true;
};

export const downloadMobilisationExport = (payload) => {
  const data = readExportPayload(payload);
  if (!data || data.type !== "mobilisation") return false;
  return downloadSocketExport(payload);
};

const handleExportCompleted = (payload) => {
  try {
    downloadSocketExport(payload);
  } catch (error) {
    console.error("Failed to download socket export:", error);
    const data = readExportPayload(payload);
    const failedEvent =
      EXPORT_FAILED_EVENTS[data?.type] ||
      (exportWatchActive ? EXPORT_FAILED_EVENTS.report : null);
    if (typeof window !== "undefined" && failedEvent) {
      window.dispatchEvent(new CustomEvent(failedEvent, { detail: error }));
    }
  }
};

const ensureExportListener = (s) => {
  if (!s) return;
  EXPORT_SOCKET_EVENTS.forEach((eventName) => {
    s.off(eventName, handleExportCompleted);
    s.on(eventName, handleExportCompleted);
  });
  if (!s.__cinchyExportAny) {
    s.__cinchyExportAny = true;
    s.onAny((eventName, ...args) => {
      const name = String(eventName || "");
      if (!/export/i.test(name) || EXPORT_SOCKET_EVENTS.includes(name)) return;
      args.forEach((arg) => handleExportCompleted(arg));
    });
  }
};

const attachLifecycleListeners = (s) => {
  ensureExportListener(s);

  s.on("connect", () => {
    connectedUserId = s.auth?.userId || getUserId() || null;
    ensureExportListener(s);
    console.log("🟢 Connected to server");
    console.log("Socket ID:", s.id);
    if (serverReconnectTimer) {
      clearTimeout(serverReconnectTimer);
      serverReconnectTimer = null;
    }
    // New connection must re-join; clear active so emit is not skipped.
    activelyJoinedRoomId = null;
    emitJoinIfNeeded(s);
  });

  s.on("connect_error", (err) => {
    console.log("❌ Connection error:", err?.message);
  });

  s.on("disconnect", (reason) => {
    console.log("🔴 Disconnected from server:", reason);
    connectedUserId = null;
    activelyJoinedRoomId = null;
    // Server closed the namespace. Do not close the engine here: that aborts
    // the current poll and closes the next session as soon as it opens,
    // which produces a continuous socket.io request loop.
    if (reason === "io server disconnect" && !serverReconnectTimer) {
      const delay = exportWatchActive ? 400 : 2000;
      serverReconnectTimer = setTimeout(() => {
        serverReconnectTimer = null;
        if (!s.connected) {
          if (exportWatchActive) lastManualConnectTs = 0;
          safeConnect(s, "server-disconnect");
        }
      }, delay);
    }
  });

  if (s.io && typeof s.io.on === "function") {
    s.io.on("reconnect", (attempt) => {
      console.log("✅ socket reconnected after", attempt, "attempt(s)");
      ensureExportListener(s);
    });
    s.io.on("reconnect_attempt", (attempt) => {
      console.log("🔄 socket reconnect_attempt:", attempt);
    });
    s.io.on("reconnect_failed", () => {
      console.warn("⚠️ socket reconnect_failed — will retry via health check");
      // Manager gave up; allow a fresh connect soon.
      lastManualConnectTs = 0;
    });
  }
};

/** Join a room once; duplicate calls for the same room are ignored. */
export const joinRoom = (roomId) => {
  const id = roomId ? String(roomId).trim() : "";
  if (!id) return;

  desiredRoomId = id;
  ensureSocketConnected();
  emitJoinIfNeeded();
};

export const leaveRoom = (roomId) => {
  const id = roomId ? String(roomId).trim() : "";
  if (!socket || !id) return;

  if (activelyJoinedRoomId === id || desiredRoomId === id) {
    if (socket.connected) {
      console.log("📤 leave-room:", id);
      socket.emit("leave-room", id);
    }
    if (desiredRoomId === id) desiredRoomId = null;
    if (activelyJoinedRoomId === id) activelyJoinedRoomId = null;
  }
};

const startUserIdPolling = () => {
  if (userIdPollTimer) return;
  userIdPollTimer = setInterval(() => {
    const userId = getUserId();
    if (!userId) return;

    clearInterval(userIdPollTimer);
    userIdPollTimer = null;

    if (!socket) {
      socket = createSocketInstance(userId);
      return;
    }

    applyUserIdToSocket(socket, userId);
    safeConnect(socket, "userid-ready");
  }, USERID_POLL_INTERVAL_MS);
  if (typeof window !== "undefined") {
    window.__cinchySocketUserPoll = userIdPollTimer;
  }
};

const startHealthCheck = () => {
  if (healthCheckTimer) return;
  healthCheckTimer = setInterval(() => {
    const userId = getUserId();
    if (!userId || !socket) return;

    applyUserIdToSocket(socket, userId);

    // Only reconnect when truly disconnected — never force disconnect/reconnect
    // while connected (that caused endless polling GET storms).
    if (!socket.connected && !isSocketBusy(socket)) {
      safeConnect(socket, "health-check");
    }
  }, HEALTH_CHECK_INTERVAL_MS);
  if (typeof window !== "undefined") {
    window.__cinchySocketHealthTimer = healthCheckTimer;
  }
};

const createSocketInstance = (userId) => {
  const s = io(URL, {
    query: userId ? { userId } : undefined,
    auth: userId ? { userId } : undefined,
    autoConnect: !!userId,
    // Polling first is more reliable behind proxies; websocket upgrades after.
    // Storms were caused by force disconnect/reconnect, not by this order.
    transports: ["polling", "websocket"],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 8000,
    reconnectionAttempts: Infinity,
    timeout: 30000,
    rememberUpgrade: false,
  });
  attachLifecycleListeners(s);
  return s;
};

const SOCKET_GLOBAL_KEY = "__cinchyConversationSocket";

if (typeof window !== "undefined") {
  if (window.__cinchySocketHealthTimer) {
    clearInterval(window.__cinchySocketHealthTimer);
    window.__cinchySocketHealthTimer = null;
  }
  if (window.__cinchySocketUserPoll) {
    clearInterval(window.__cinchySocketUserPoll);
    window.__cinchySocketUserPoll = null;
  }
  if (window[SOCKET_GLOBAL_KEY]) {
    try {
      window[SOCKET_GLOBAL_KEY].removeAllListeners();
      window[SOCKET_GLOBAL_KEY].io?.reconnection(false);
      window[SOCKET_GLOBAL_KEY].disconnect();
    } catch (_) {}
    window[SOCKET_GLOBAL_KEY] = null;
  }
}

socket = createSocketInstance(getUserId());

if (typeof window !== "undefined") {
  window[SOCKET_GLOBAL_KEY] = socket;
}

if (!getUserId()) {
  startUserIdPolling();
}
startHealthCheck();

export const ensureSocketConnected = () => {
  const userId = getUserId();
  if (!socket) {
    socket = createSocketInstance(userId);
    ensureExportListener(socket);
    return socket;
  }
  applyUserIdToSocket(socket, userId);
  ensureExportListener(socket);
  if (userId && !socket.connected) {
    safeConnect(socket, "ensure");
  }
  return socket;
};

/** Resolve only after this user's socket is connected, so export events are not missed. */
export const waitForSocketConnected = (timeoutMs = 20000) =>
  new Promise((resolve, reject) => {
    const userId = getUserId();
    if (!userId) {
      reject(new Error("Not signed in"));
      return;
    }

    if (!socket) {
      socket = createSocketInstance(userId);
    }
    applyUserIdToSocket(socket, userId);
    ensureExportListener(socket);

    let settled = false;
    let stallTimer = null;
    const finish = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      clearTimeout(stallTimer);
      socket.off("connect", onConnect);
      socket.io?.off("reconnect", onReconnect);
      socket.io?.off("reconnect_failed", onReconnectFailed);
      if (error) reject(error);
      else resolve(socket);
    };

    const onConnect = () => {
      connectedUserId = socket.auth?.userId || userId;
      ensureExportListener(socket);
      finish();
    };
    const onReconnect = () => onConnect();
    const onReconnectFailed = () => {
      lastManualConnectTs = 0;
      try {
        socket.connect();
      } catch (_) {}
    };

    const timer = setTimeout(() => {
      finish(new Error("Socket connection timed out"));
    }, timeoutMs);

    socket.on("connect", onConnect);
    socket.io?.on("reconnect", onReconnect);
    socket.io?.on("reconnect_failed", onReconnectFailed);

    const authId = socket.auth?.userId || connectedUserId;
    if (socket.connected && (!authId || authId === userId)) {
      connectedUserId = userId;
      finish();
      return;
    }

    if (socket.connected && authId && authId !== userId) {
      lastManualConnectTs = 0;
      socket.disconnect();
    }

    if (!isSocketBusy(socket)) {
      lastManualConnectTs = 0;
      safeConnect(socket, "export");
    }

    stallTimer = setTimeout(() => {
      if (settled || socket.connected || isSocketBusy(socket)) return;
      lastManualConnectTs = 0;
      safeConnect(socket, "export-stall");
    }, 4000);
  });

const connectIfIdle = (reason) => {
  const userId = getUserId();
  if (!userId) return;
  if (!socket) {
    socket = createSocketInstance(userId);
    return;
  }
  applyUserIdToSocket(socket, userId);
  ensureExportListener(socket);
  if (socket.connected || isSocketBusy(socket)) return;
  lastManualConnectTs = 0;
  safeConnect(socket, reason);
};

const beginExportWatch = (fileType) => {
  exportWatchActive = true;
  pendingExportFileType = normalizeFileType(fileType) || null;
  if (typeof window !== "undefined") window.__cinchyLastSocketExport = null;
  connectIfIdle("export-watch");
  if (exportWatchTimer) clearInterval(exportWatchTimer);
  exportWatchTimer = setInterval(() => {
    if (!exportWatchActive) return;
    connectIfIdle("export-watchdog");
  }, EXPORT_WATCH_INTERVAL_MS);
};

const endExportWatch = () => {
  exportWatchActive = false;
  pendingExportFileType = null;
  if (exportWatchTimer) {
    clearInterval(exportWatchTimer);
    exportWatchTimer = null;
  }
};

const waitForReportDownload = (timeoutMs) => {
  let settled = false;
  let timer = null;
  let finish = () => {};

  const promise = new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener("cinchy-report-export-downloaded", onDownloaded);
      window.removeEventListener("cinchy-report-export-failed", onFailed);
    };

    finish = (error, value) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (error) reject(error);
      else resolve(value);
    };

    const onDownloaded = (event) => finish(null, event?.detail || true);
    const onFailed = () => finish(new Error("Failed to download the report. Please try again."));

    timer = setTimeout(() => {
      finish(new Error("Report export timed out. Please try again."));
    }, timeoutMs);

    window.addEventListener("cinchy-report-export-downloaded", onDownloaded);
    window.addEventListener("cinchy-report-export-failed", onFailed);
  });

  promise.catch(() => {});
  promise.cancel = (error) => finish(error || new Error("Report export cancelled"));
  return promise;
};

const publishInlineReport = (response, fileType) => {
  const inline = readExportPayload(response?.data?.data ?? response?.data ?? response);
  if (!inline?.base64) return false;
  return downloadSocketExport({
    ...inline,
    type: inline.type && inline.type !== "export" ? inline.type : "report",
    fileType: inline.fileType || fileType,
  });
};

/** Keep the socket up until the PDF or Excel file has actually downloaded. */
export const requestReportDownload = async (request, options = {}) => {
  const timeoutMs = options.timeoutMs || REPORT_EXPORT_TIMEOUT_MS;
  const fileType = normalizeFileType(options.fileType) || "xlsx";
  const startedAt = Date.now();
  beginExportWatch(fileType);

  const downloadPromise = waitForReportDownload(timeoutMs);
  let received = false;
  downloadPromise.then(
    () => {
      received = true;
    },
    () => {
      received = true;
    }
  );

  const remaining = () => Math.max(1000, timeoutMs - (Date.now() - startedAt));
  let retried = false;
  let onDisconnect = null;
  let liveSocket = null;

  const retryAfterReconnect = async () => {
    if (retried || received || !exportWatchActive) return;
    retried = true;
    try {
      await waitForSocketConnected(Math.min(30000, remaining()));
      if (received || !exportWatchActive) return;
      const response = await request();
      if (response?.data?.isSuccess === false) return;
      publishInlineReport(response, fileType);
    } catch (error) {
      console.warn("report export retry failed:", error?.message || error);
    }
  };

  try {
    liveSocket = await waitForSocketConnected(Math.min(30000, remaining()));
    onDisconnect = () => {
      retryAfterReconnect();
    };
    liveSocket.on("disconnect", onDisconnect);

    const response = await request();
    if (response?.data?.isSuccess === false) {
      const error = new Error(response?.data?.message || "Failed to export report");
      error.response = { data: response.data, status: response.status };
      throw error;
    }

    const downloadedInline = publishInlineReport(response, fileType);
    options.onResponse?.(response, {
      background: !received && !downloadedInline,
    });
    await downloadPromise;
    return response;
  } catch (error) {
    downloadPromise.cancel(error);
    throw error;
  } finally {
    if (onDisconnect && liveSocket) liveSocket.off("disconnect", onDisconnect);
    endExportWatch();
  }
};

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    console.log("🌐 network online — verifying socket");
    lastManualConnectTs = 0;
    ensureSocketConnected();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      console.log("👀 tab visible — verifying socket");
      if (exportWatchActive) lastManualConnectTs = 0;
      ensureSocketConnected();
    }
  });
}

export const sendMessage = (data) => {
  const currentUserId = getUserId();
  if (!socket) {
    console.error("❌ Socket not initialized");
    return;
  }

  const buildPayload = () => {
    const payload = {
      roomId: data?.roomId,
      message: data.message,
      senderId: currentUserId,
      type: data?.type || "Text",
    };
    if (data?.replyTo) payload.replyTo = data.replyTo;
    if (data?.caption) payload.caption = data.caption;
    if (data?.memberRemoved) payload.memberRemoved = true;
    if (data?.memberAdded) payload.memberAdded = true;
    return payload;
  };

  if (!socket.connected) {
    console.warn("⚠️ Socket not connected, attempting to connect...");
    ensureSocketConnected();
    socket.once("connect", () => {
      socket.emit("send-message", buildPayload());
    });
    return;
  }

  console.log("📤 Sending message:", data);
  socket.emit("send-message", buildPayload());
};

export { socket };
